/**
 * Reiseorganisation einer Betreuungskraft — Übernahme aus CRM 1.0
 * (ProfileCares.js, Fenster „Reiseorganisationen", nur BK).
 *
 * Bewusst gegen die unveränderten CRM-1.0-Endpunkte:
 *   GET    /v1/admin/user/{id}/travels   (paginiert, Filter showOldTravels)
 *   POST   /v1/admin/travel
 *   GET    /v1/admin/travel/{id}
 *   PUT    /v1/admin/travel/{id}
 *   DELETE /v1/admin/travel/{id}
 *
 * Die API erwartet Datum und Uhrzeit getrennt (abfahrtsdatum + abfahrtszeit,
 * ankunftsdatum + ankunftszeit) und speichert `abfahrt`/`ankunft` als
 * Zeitstempel. „Vergangene Reisen" sind solche, deren Ankunft länger als
 * 12 Stunden zurückliegt (Server-Logik).
 */
import { apiV1 } from './api';
import { primaryProfile, type V1User } from './v1';
import { HARDCODED } from '../pages/pflege/profile/options';

export type TransferType = 'car' | 'bus' | 'train' | 'plane';

/** Transferarten wie CRM 1.0 (Wert → i18n-Key). */
export const TRANSFER_TYPES: { value: TransferType; labelKey: string }[] = [
  { value: 'car',   labelKey: 'betreuung.travels.transfer.car' },
  { value: 'bus',   labelKey: 'betreuung.travels.transfer.bus' },
  { value: 'train', labelKey: 'betreuung.travels.transfer.train' },
  { value: 'plane', labelKey: 'betreuung.travels.transfer.plane' },
];

export interface Travel {
  id: number;
  user_id: number;
  /** „YYYY-MM-DD HH:MM:SS" bzw. ISO. */
  abfahrt: string | null;
  ankunft: string | null;
  abfahrtsadresse: string | null;
  ankunftsadresse: string | null;
  transferart: string | null;
  transfergesellschaft: string | null;
  transferid: string | null;
  kommentar: string | null;
  created_at?: string;
  updated_at?: string;
}

/** Formularwerte — Datum und Uhrzeit getrennt, wie die v1-API sie erwartet. */
export interface TravelInput {
  abfahrtsdatum: string;
  abfahrtszeit: string;
  ankunftsdatum: string;
  ankunftszeit: string;
  abfahrtsadresse: string;
  ankunftsadresse: string;
  transferart: string;
  transfergesellschaft: string;
  transferid: string;
  kommentar: string;
}

export const EMPTY_TRAVEL_INPUT: TravelInput = {
  abfahrtsdatum: '', abfahrtszeit: '', ankunftsdatum: '', ankunftszeit: '',
  abfahrtsadresse: '', ankunftsadresse: '', transferart: '',
  transfergesellschaft: '', transferid: '', kommentar: '',
};

/** „2026-10-11 08:30:00" / ISO → { date: 2026-10-11, time: 08:30 } (lokale Wandzeit). */
export function splitDateTime(v: string | null | undefined): { date: string; time: string } {
  if (!v) return { date: '', time: '' };
  const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(String(v));
  if (m) return { date: m[1], time: m[2] };
  const d = /^(\d{4}-\d{2}-\d{2})$/.exec(String(v));
  return d ? { date: d[1], time: '' } : { date: '', time: '' };
}

export function travelToInput(t: Travel): TravelInput {
  const ab = splitDateTime(t.abfahrt);
  const an = splitDateTime(t.ankunft);
  return {
    abfahrtsdatum: ab.date, abfahrtszeit: ab.time,
    ankunftsdatum: an.date, ankunftszeit: an.time,
    abfahrtsadresse: t.abfahrtsadresse ?? '',
    ankunftsadresse: t.ankunftsadresse ?? '',
    transferart: t.transferart ?? '',
    transfergesellschaft: t.transfergesellschaft ?? '',
    transferid: t.transferid ?? '',
    kommentar: t.kommentar ?? '',
  };
}

/**
 * Vorbelegung der Abfahrtsadresse mit der Adresse der BK (CRM 1.0
 * `defaultAdress()`): Straße Hausnummer / PLZ Ort / Land.
 */
export function defaultDepartureAddress(user: V1User | null): string {
  if (!user) return '';
  const p = primaryProfile(user);
  const pick = (k: 'strasse' | 'hausnummer' | 'plz' | 'ort' | 'land'): string => {
    const own = (user as unknown as Record<string, unknown>)[k];
    const v = (p as unknown as Record<string, unknown> | null)?.[k] ?? own;
    return typeof v === 'string' ? v.trim() : v != null ? String(v) : '';
  };
  const land = pick('land');
  const landLabel = HARDCODED.land.find(o => o.value === land)?.label ?? land;
  return [
    [pick('strasse'), pick('hausnummer')].filter(Boolean).join(' '),
    [pick('plz'), pick('ort')].filter(Boolean).join(' '),
    landLabel,
  ].join('\n');
}

interface Pagination<T> { data: T[]; total: number; current_page: number; last_page: number }

export const v1Travels = {
  /** GET /v1/admin/user/{id}/travels — neueste Abfahrt zuerst. */
  listForUser: async (userId: number, showOld: boolean): Promise<{ rows: Travel[]; total: number }> => {
    const res = await apiV1.get<{ type: string; pagination: Pagination<Travel> }>(
      `/admin/user/${userId}/travels`,
      { params: { perPage: 100, page: 1, 'filters[showOldTravels]': showOld ? 'true' : 'false' } },
    );
    const p = res.data.pagination;
    return { rows: p?.data ?? [], total: p?.total ?? (p?.data?.length ?? 0) };
  },
  create: async (userId: number, input: TravelInput): Promise<Travel> => {
    const res = await apiV1.post<{ type: string; travel: Travel }>('/admin/travel', { user_id: userId, ...input });
    return res.data.travel;
  },
  update: async (id: number, input: TravelInput): Promise<Travel> => {
    const res = await apiV1.put<{ type: string; travel: Travel }>(`/admin/travel/${id}`, input);
    return res.data.travel;
  },
  delete: async (id: number): Promise<void> => {
    await apiV1.delete(`/admin/travel/${id}`);
  },
};
